import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getFamilias } from "../services/familiasService";
import { getPlanes } from "../services/planesService";
import { getUrls } from "../services/urlsService";
import { Card, CardContent } from "../components/ui/card";
import { ArrowRight, Users, Package, Link as LinkIcon, Activity } from "lucide-react";
import SessionsAreaChart from "../components/charts/SessionsAreaChart";
import CotizacionesChart from "../components/charts/CotizacionesChart";
import ContratacionesChart from "../components/charts/ContratacionesChart";
import OrdenesPagoChart from "../components/charts/OrdenesPagoChart";

/* ─── Credit-card stat card ──────────────────────────────────────────── */
function CreditCard({ gradient, icon: Icon, label, value, sub, to }) {
  return (
    <Link to={to} className="block group">
      <div
        className={`relative rounded-[22px] overflow-hidden border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.22)] aspect-[1.586] bg-gradient-to-br ${gradient} transition-transform duration-200 group-hover:scale-[1.02] active:scale-[0.99]`}
      >
        {/* chip decorativo */}
        <div className="absolute top-5 left-6 w-9 h-7 rounded-md bg-white/20 border border-white/30 backdrop-blur-sm" />

        {/* logo / icono */}
        <div className="absolute top-5 right-6 p-2 rounded-xl bg-white/15 border border-white/20">
          <Icon className="h-5 w-5 text-white" />
        </div>

        {/* contenido */}
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-5">
          <p className="text-white/65 text-xs font-semibold uppercase tracking-widest mb-0.5">{label}</p>
          <p className="text-white text-4xl font-extrabold tabular-nums tracking-tight leading-none">{value}</p>
          {sub && <p className="text-white/60 text-xs mt-1.5 font-medium">{sub}</p>}
        </div>

        {/* flecha acción */}
        <div className="absolute bottom-5 right-6 p-1.5 rounded-full bg-white/15 border border-white/20 group-hover:bg-white/25 transition-colors">
          <ArrowRight className="h-3.5 w-3.5 text-white" />
        </div>

        {/* brillo diagonal sutil */}
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent pointer-events-none" />
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState({
    familias: { total: 0, active: 0, inactive: 0 },
    planes: { total: 0, active: 0, inactive: 0 },
    urls: { total: 0, byType: {} }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [familiasData, planesData, urlsData] = await Promise.all([
          getFamilias(),
          getPlanes(),
          getUrls()
        ]);
        setStats({
          familias: {
            total: familiasData.length,
            active: familiasData.filter(f => f.status === 'active').length,
            inactive: familiasData.filter(f => f.status !== 'active').length
          },
          planes: {
            total: planesData.length,
            active: planesData.filter(p => p.status === 'active').length,
            inactive: planesData.filter(p => p.status !== 'active').length
          },
          urls: {
            total: urlsData.length,
            byType: urlsData.reduce((acc, url) => {
              const type = url.tipo || 'other';
              acc[type] = (acc[type] || 0) + 1;
              return acc;
            }, {})
          }
        });
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-[#007D6E] mx-auto" />
          <p className="text-sm text-muted-foreground">Cargando dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">

      {/* ── Header simple ──────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Activity className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Panel de Control</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">
            Bienvenido a <span className="fancyyool-highlight-teal">Yool Admin</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Gestioná productos, planes y familias desde un solo lugar.
          </p>
        </div>
      </div>

      {/* ── Credit Cards ────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <CreditCard
          gradient="from-[#5B2BE5] via-[#7D7CFF] to-[#2563EB]"
          icon={Users}
          label="Familias de Seguros"
          value={stats.familias.active + stats.familias.inactive}
          sub={`${stats.familias.active} activas`}
          to="/admin/familias"
        />
        <CreditCard
          gradient="from-[#06b6d4] via-[#2563EB] to-[#007D6E]"
          icon={Package}
          label="Planes de Seguros"
          value={stats.planes.active + stats.planes.inactive}
          sub={`${stats.planes.active} activos`}
          to="/admin/planes"
        />
        <CreditCard
          gradient="from-[#E91E63] via-[#a855f7] to-[#5B2BE5]"
          icon={LinkIcon}
          label="Enlaces y URLs"
          value={stats.urls.total}
          sub={`${Object.keys(stats.urls.byType).length} tipos`}
          to="/urls"
        />
      </div>

      {/* ── Charts ───────────────────────────────────────────── */}
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Análisis y Métricas</h2>
          <p className="text-sm text-muted-foreground">Rendimiento del negocio en tiempo real</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card><CardContent className="pt-6"><CotizacionesChart /></CardContent></Card>
          <Card><CardContent className="pt-6"><ContratacionesChart /></CardContent></Card>
        </div>

        <Card><CardContent className="pt-6"><OrdenesPagoChart /></CardContent></Card>
        <Card><CardContent className="pt-6"><SessionsAreaChart /></CardContent></Card>
      </div>
    </div>
  );
}
